import { z } from 'zod';
import type { UserRef } from './tasks.js';

// ---- Замещение ----

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Укажите дату');

export const substitutionSchema = z
  .object({
    /** Кого замещают; по умолчанию — себя. */
    userId: z.uuid().optional(),
    deputyId: z.uuid(),
    dateFrom: isoDate,
    dateTo: isoDate,
    reason: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.dateTo >= v.dateFrom, { message: 'Дата окончания раньше начала', path: ['dateTo'] });
export type SubstitutionInput = z.input<typeof substitutionSchema>;

export interface SubstitutionDto {
  id: string;
  user: UserRef;
  deputy: UserRef;
  dateFrom: string;
  dateTo: string;
  reason: string | null;
  active: boolean;
  fromTask: boolean;
}

// ---- Новости ----

/** Где публикация: в ленте новостей, в верхней панели или и там, и там. */
export const NEWS_PLACEMENTS = ['news', 'bar', 'both'] as const;
export type NewsPlacement = (typeof NEWS_PLACEMENTS)[number];
export const NEWS_PLACEMENT_LABELS: Record<NewsPlacement, string> = { news: 'Новость', bar: 'Верхняя панель', both: 'И там, и там' };

/** Форма края, по которому растворяется фон панели. */
export const BAR_FADE_SHAPES = ['straight', 'diagonal', 'wave', 'oval'] as const;
export type BarFadeShape = (typeof BAR_FADE_SHAPES)[number];
export const BAR_FADE_SHAPE_LABELS: Record<BarFadeShape, string> = { straight: 'Прямой', diagonal: 'Наклонный', wave: 'Волна', oval: 'Овал' };

/** Фон панели: картинка, GIF или видео до 10 МБ. */
export const BAR_MEDIA_MAX_MB = 10;
export const BAR_MEDIA_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm';

export const barFadeSchema = z.object({
  left: z.boolean(),
  right: z.boolean(),
  bottom: z.boolean(),
  /** Ширина растворения, px: на сколько от края картинка проявляется. */
  depth: z.number().int().min(8).max(240),
  shape: z.enum(BAR_FADE_SHAPES),
});
export type BarFade = z.infer<typeof barFadeSchema>;
export const DEFAULT_BAR_FADE: BarFade = { left: true, right: true, bottom: false, depth: 72, shape: 'straight' };

export const barSchema = z.object({
  /** Строка в панели; на телефоне видна только она. */
  text: z.string().trim().min(1, 'Напишите текст для панели').max(60, 'Не длиннее 60 знаков'),
  /** Подпись рядом — только на компьютере. */
  caption: z.string().trim().max(80, 'Не длиннее 80 знаков').default(''),
  /** Дата события (ГГГГ-ММ-ДД) — для пометки «через 2 дня / сегодня». */
  eventDate: isoDate.nullable().default(null),
  countdown: z.boolean().default(true),
  /** Сотрудник может скрыть публикацию у себя. */
  dismissible: z.boolean().default(true),
  /** Какая часть кадра видна по вертикали, 0 — верх, 100 — низ. */
  pos: z.number().int().min(0).max(100).default(50),
  /** Затемнение под текстом, %. */
  dim: z.number().int().min(0).max(90).default(55),
  fade: barFadeSchema.default(DEFAULT_BAR_FADE),
});
export type BarSettings = z.output<typeof barSchema>;

const isoDateTime = z.iso.datetime({ offset: true, message: 'Укажите дату и время' });

export const newsSchema = z
  .object({
    title: z.string().trim().min(1, 'Укажите заголовок').max(300),
    /** Для публикации только в панели текст необязателен: без него нажатие ничего не открывает. */
    body: z.string().trim().max(20_000).default(''),
    pinned: z.boolean().default(false),
    archived: z.boolean().default(false),
    /** Дата новости (ГГГГ-ММ-ДД); не указана — сегодня, при правке — прежняя. */
    publishedAt: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Укажите дату')
      .refine((d) => d >= '2000-01-01' && d <= '2100-12-31' && !Number.isNaN(Date.parse(d)), 'Некорректная дата')
      .optional(),
    placement: z.enum(NEWS_PLACEMENTS).default('news'),
    bar: barSchema.optional(),
    /** Показ в панели: с какого момента (нет — сразу) и до какого (нет — пока не заменят). */
    barFrom: isoDateTime.nullable().optional(),
    barUntil: isoDateTime.nullable().optional(),
    /** В это время в панели уже есть публикация — завершить её показ и показывать эту. */
    barReplace: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.placement !== 'bar' && !v.body) ctx.addIssue({ code: 'custom', path: ['body'], message: 'Напишите текст' });
    if (v.placement !== 'news' && !v.bar) ctx.addIssue({ code: 'custom', path: ['bar', 'text'], message: 'Напишите текст для панели' });
    if (v.barFrom && v.barUntil && Date.parse(v.barUntil) <= Date.parse(v.barFrom)) {
      ctx.addIssue({ code: 'custom', path: ['barUntil'], message: 'Окончание показа — позже начала' });
    }
  });
export type NewsInput = z.input<typeof newsSchema>;

export interface NewsBarDto extends BarSettings {
  from: string | null;
  until: string | null;
  media: { url: string; mime: string; name: string; size: number } | null;
}

export interface NewsDto {
  id: string;
  title: string;
  body: string;
  pinned: boolean;
  publishedAt: string;
  archived: boolean;
  author: UserRef | null;
  placement: NewsPlacement;
  bar: NewsBarDto | null;
}

/** Публикация, которая в это время занимает панель (при сохранении новой — вопрос «заменить или запланировать»). */
export interface NewsBarBusyDto {
  id: string;
  title: string;
  text: string;
  from: string | null;
  until: string | null;
}

// ---- Отчёты ----

export const reportQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  departmentId: z.uuid().optional(),
});
export type ReportQuery = z.input<typeof reportQuerySchema>;

export const REPORTS = ['summary', 'approvers', 'routes', 'stages', 'subtasks', 'activity', 'hanging', 'trips'] as const;
export type ReportName = (typeof REPORTS)[number];

export const REPORT_TITLES: Record<ReportName, string> = {
  summary: 'Сводка',
  approvers: 'Согласующие',
  routes: 'Маршруты',
  stages: 'Этапы: где задерживаются',
  subtasks: 'Подзадачи',
  activity: 'Активность пользователей',
  hanging: 'Висит сейчас',
  trips: 'Командировки',
};

export interface SummaryReport {
  created: number;
  launched: number;
  approved: number;
  returned: number;
  rejected: number;
  cancelled: number;
  /** Среднее время от запуска до итога, часы. */
  avgApprovalHours: number | null;
  /** Среднее время решения согласующего, часы. */
  avgDecisionHours: number | null;
  overdueSteps: number;
  pendingNow: number;
  assignmentsDone: number;
  assignmentsOverdue: number;
  byDay: Array<{ day: string; created: number; approved: number }>;
}

export interface ApproverRow {
  user: UserRef;
  department: string | null;
  decided: number;
  approved: number;
  declined: number;
  rejected: number;
  avgHours: number | null;
  maxHours: number | null;
  overdue: number;
  pendingNow: number;
  /** Дольше всего ждёт решения сейчас, часы. */
  oldestPendingHours: number | null;
}

export interface RouteRow {
  route: string;
  launched: number;
  approved: number;
  returned: number;
  rejected: number;
  avgHours: number | null;
  /** Самый медленный этап маршрута. */
  slowestStage: string | null;
  slowestStageHours: number | null;
}

export interface StageRow {
  route: string;
  stage: string;
  decisions: number;
  avgHours: number | null;
  declines: number;
  overdue: number;
}

export interface SubtaskRow {
  /** Автор основной задачи (инициатор). */
  initiator: UserRef;
  tasks: number;
  subtasks: number;
  /** Кто чаще всего ставит подзадачи по задачам этого инициатора. */
  topCreators: Array<{ user: UserRef; count: number }>;
  returnedForRework: number;
}

export interface ActivityRow {
  user: UserRef;
  department: string | null;
  lastSeenAt: string | null;
  logins: number;
  created: number;
  decisions: number;
  comments: number;
  answered: number;
  pendingNow: number;
}

export interface HangingRow {
  taskId: string;
  number: number;
  title: string;
  route: string | null;
  stage: string;
  user: UserRef;
  activatedAt: string;
  dueAt: string | null;
  hours: number;
  overdue: boolean;
}

export interface TripRow {
  month: string;
  department: string | null;
  trips: number;
  days: number;
  total: number;
}

export interface ReportsDto {
  summary: SummaryReport;
  approvers: ApproverRow[];
  routes: RouteRow[];
  stages: StageRow[];
  subtasks: SubtaskRow[];
  activity: ActivityRow[];
  hanging: HangingRow[];
  trips: TripRow[];
}
